import React from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {useNavigate} from 'react-router';
import {useIsRescoring, useRefreshAccounts} from '../../hooks/api/scoringInfo';
import {ObjectId} from 'bson';

type ActivateMarketButtonProps = ButtonProps & {activeMarketId: ObjectId};

export const ActivateMarketButton = ({
  activeMarketId,
  ...rest
}: ActivateMarketButtonProps) => {
  const publishAndScoreModal = useDisclosure();
  const isRescoring = useIsRescoring();

  return (
    <>
      <Tooltip
        label="Please wait until rescoring has finished"
        placement="bottom-start"
        isDisabled={!isRescoring}
      >
        <Button
          colorScheme="kbuttonblue"
          variant="ghost"
          fontWeight="normal"
          flexShrink="0"
          isDisabled={isRescoring}
          onClick={publishAndScoreModal.onOpen}
          {...rest}
        >
          Make Active
        </Button>
      </Tooltip>
      <ActivateMarketModal
        activeMarketId={activeMarketId}
        isOpen={publishAndScoreModal.isOpen}
        onClose={publishAndScoreModal.onClose}
      />
    </>
  );
};

const ActivateMarketModal = ({
  activeMarketId,
  isOpen,
  onClose,
}: {
  activeMarketId: ObjectId;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const triggerRefresh = useRefreshAccounts();

  const publishAndRescore = () =>
    triggerRefresh.mutate(
      {activeMarketId},
      {
        onSuccess: () => {
          onClose();
          navigate('/discover/recommended');
        },
      }
    );

  return (
    <>
      <Modal isCentered={true} isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontWeight="normal">Switch markets?</ModalHeader>
          <ModalBody as={Flex} flexDirection="column" p="0">
            <Box px="6" pt="2" pb="4">
              <Text>What happens next:</Text>
              <List spacing="2" p="2">
                <ListItem>✅ New Discover accounts will be loaded.</ListItem>
                <ListItem>🧮 Account scores will be re-calculated.</ListItem>
                <ListItem>⏳ This may take a few minutes to finish.</ListItem>
              </List>
            </Box>
          </ModalBody>
          <ModalFooter
            bgColor="kgray.100"
            borderTop="1px solid"
            borderColor="kgray.200"
            borderBottomRadius="md"
            gap="4"
            justifyContent="start"
          >
            <Button
              colorScheme="kbuttonblue"
              fontWeight="normal"
              isLoading={triggerRefresh.isPending}
              onClick={publishAndRescore}
            >
              Confirm
            </Button>
            <Button onClick={onClose} fontWeight="normal" variant="ghost">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
